import { uiText } from '../../i18n/uiText';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Home,
  KeyRound,
  LogOut,
  Mail,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sun,
  X,
} from 'lucide-react';
import { useAuth } from '../../store/AuthContext';
import {
  getBreadcrumbs,
  getCurrentModule,
  getVisibleModules,
  getVisibleUtilityLinks,
} from '../../config/navigation';
import NotificationsBell from './NotificationsBell';
import OnlinePresencePill from './OnlinePresencePill';
import { canAccessResource, isAuditeeRole } from '../../utils/permissions';
import CommandPalette from '../common/CommandPalette';
import InterfaceLanguage from '../common/InterfaceLanguage';

const THEME_STORAGE_KEY = 'audit_pro_theme';

function getInitialTheme() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

const routeMatches = (pathname, target) => (
  pathname === target || pathname.startsWith(`${target}/`)
);

function SidebarLink({
  to,
  icon: Icon,
  label,
  description,
  collapsed,
  pathname,
  onClick,
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={({ isActive }) => `workspace-sidebar-link ${isActive || routeMatches(pathname, to) ? 'active' : ''}`}
    >
      <span className="workspace-sidebar-link-icon"><Icon size={17} /></span>
      {!collapsed ? (
        <span className="workspace-sidebar-link-copy">
          <span className="workspace-sidebar-link-label">{label}</span>
          {description ? <span className="workspace-sidebar-link-description">{description}</span> : null}
        </span>
      ) : null}
    </NavLink>
  );
}

function groupSidebarLinks(links) {
  return links.reduce((result, item) => {
    const group = item.group || uiText("Раздел");
    if (!result[group]) result[group] = [];
    result[group].push(item);
    return result;
  }, {});
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('workspace_sidebar_collapsed') === '1',
  );
  const [theme, setTheme] = useState(getInitialTheme);
  const isAuditee = isAuditeeRole(user);
  useEffect(() => {
    const shortcut = event => {
      if (event.isComposing || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      if (mobileNavOpen || isAuditee || !canAccessResource(user, 'search')) return;
      if (document.querySelector('dialog[open]:not(.ap-command-palette)')) return;
      event.preventDefault(); setCommandOpen(value => !value);
    };
    document.addEventListener('keydown', shortcut);
    return () => document.removeEventListener('keydown', shortcut);
  }, [mobileNavOpen, isAuditee, user]);

  const modules = useMemo(() => getVisibleModules(user?.role), [user?.role]);
  const currentModule = useMemo(
    () => getCurrentModule(location.pathname, user?.role),
    [location.pathname, user?.role],
  );
  const breadcrumbs = useMemo(
    () => getBreadcrumbs(location.pathname, user?.role),
    [location.pathname, user?.role],
  );
  const utilityLinks = useMemo(
    () => getVisibleUtilityLinks(location.pathname, user?.role, currentModule?.key),
    [currentModule?.key, location.pathname, user?.role],
  );

  // Global search belongs to the Audits module and must keep its contextual
  // navigation visible. Only truly standalone utility pages hide the sidebar.
  // The e-mail hub is part of the Management module (it is also linked from
  // the top bar), so it must preserve that module's contextual navigation.
  // Notifications and Help remain genuinely standalone utility screens.
  const isStandaloneUtilityPage = ['/notifications', '/help'].includes(location.pathname);
  const sidebarLinks = useMemo(() => {
    if (isStandaloneUtilityPage) return [];
    const entries = [...(currentModule?.children || [])];
    utilityLinks.forEach((item) => {
      if (!entries.some((entry) => entry.to === item.to)) {
        entries.push({ ...item, group: uiText("Рабочие инструменты") });
      }
    });
    return entries;
  }, [currentModule, isStandaloneUtilityPage, utilityLinks]);
  const sidebarGroups = useMemo(() => groupSidebarLinks(sidebarLinks), [sidebarLinks]);
  const showSidebar = !isAuditee && sidebarLinks.length > 0;

  useEffect(() => {
    setMenuOpen(false);
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen || !window.matchMedia('(max-width: 840px)').matches) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  useEffect(() => {
    localStorage.setItem('workspace_sidebar_collapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    document.body.dataset.theme = theme;
    root.style.colorScheme = theme;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // В приватном режиме хранилище может быть недоступно. Тема всё равно
      // применяется на время текущей сессии.
    }
  }, [theme]);

  const roleLabel = {
    admin: uiText("Администратор"),
    auditor: uiText("Аудитор"),
    analyst: uiText("Аналитик"),
    customer: uiText("Представитель организации"),
    auditee: uiText("Представитель организации"),
  }[user?.role] || user?.role || uiText("Пользователь");

  const initials = user?.full_name
    ?.split(' ')
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U';
  const canUseNotifications = canAccessResource(user, 'notifications');
  const canSearch = canAccessResource(user, 'search');

  return (
    <div className={`layout workspace-layout theme-light${theme === 'dark' ? ' theme-dark' : ''}${isAuditee ? ' auditee-layout' : ''}`}>
      <a className="skip-link" href="#main-content">{uiText("К содержимому")}</a>
      {commandOpen && <CommandPalette user={user} onClose={() => setCommandOpen(false)} />}

      <header className="workspace-topbar">
        <div className="workspace-topbar-main">
          <NavLink to="/" className="workspace-brand" aria-label={uiText("Audit Pro — главная")}>
            <span className="brand-mark brand-mark-app" aria-hidden="true">
              <img src="/audit-pro-app-icon-v1.png" alt="" />
            </span>
            <span className="workspace-brand-copy">
              <strong>Audit Pro</strong>
              <small>AAC</small>
            </span>
          </NavLink>

          <button
            type="button"
            className="workspace-mobile-toggle workspace-icon-btn"
            onClick={() => setMobileNavOpen((value) => !value)}
            aria-label={uiText("Открыть навигацию")}
            aria-expanded={mobileNavOpen}
          >
            <Menu size={18} />
          </button>

          <nav className="workspace-module-tabs" aria-label={uiText("Основные разделы")}>
            {modules.map((module) => {
              const Icon = module.icon;
              const active = !isStandaloneUtilityPage && currentModule?.key === module.key;
              return (
                <NavLink
                  key={module.key}
                  to={module.to}
                  className={`workspace-module-tab ${active ? 'active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon size={16} />
                  <span>{module.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="workspace-topbar-actions">
            {!isAuditee ? <OnlinePresencePill /> : null}
            <button
              type="button"
              className="workspace-top-action workspace-theme-toggle"
              onClick={() => setTheme((value) => (value === 'dark' ? 'light' : 'dark'))}
              aria-label={theme === 'dark' ? uiText("Включить светлую тему") : uiText("Включить тёмную тему")}
              aria-pressed={theme === 'dark'}
              title={theme === 'dark' ? uiText("Светлая тема") : uiText("Тёмная тема")}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              <span className="workspace-theme-toggle-label">{theme === 'dark' ? uiText("Светлая") : uiText("Тёмная")}</span>
            </button>
            {canSearch && !isAuditee ? (
              <button type="button" className="workspace-top-action ap-studio-command-trigger" aria-label={uiText("Быстрый поиск")} title={uiText("Быстрый поиск · Ctrl / ⌘ K")} disabled={mobileNavOpen} onClick={() => setCommandOpen(true)}>
                <Search size={16} />
                <span>{uiText("Поиск")}</span>
              </button>
            ) : null}
            <NavLink to="/outgoing-emails" className="workspace-top-action" aria-label={uiText("Сообщения")}>
              <Mail size={16} />
              <span>{uiText("Сообщения")}</span>
            </NavLink>
            {canUseNotifications ? <NotificationsBell showLabel /> : null}
            <div className="workspace-user-menu" ref={menuRef}>
              <button
                type="button"
                className="workspace-user-pill"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((value) => !value)}
              >
                <div className="user-avatar">{initials}</div>
                <div className="workspace-user-meta">
                  <div className="workspace-user-name">{user?.full_name || uiText("Пользователь")}</div>
                  <div className="workspace-user-role">{roleLabel}</div>
                </div>
                <ChevronDown size={16} />
              </button>
              {menuOpen ? (
                <div className="dropdown-menu workspace-profile-dropdown" role="menu">
                  <div className="dropdown-header workspace-profile-dropdown-header">
                    <div className="workspace-profile-summary-avatar" aria-hidden="true">{initials}</div>
                    <div className="workspace-profile-summary-copy">
                      <div className="dropdown-title">{user?.full_name || uiText("Пользователь")}</div>
                      <div className="dropdown-subtitle">{user?.email || roleLabel}</div>
                      <div className="workspace-profile-summary-role">{roleLabel}</div>
                    </div>
                  </div>
                  <NavLink to="/help" className="dropdown-item" role="menuitem">
                    <BookOpen size={15} />{uiText(" Помощь")}</NavLink>
                  <InterfaceLanguage compact />
                  <NavLink to="/security" className="dropdown-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                    <KeyRound size={15} />{uiText(" Пароль и доступ")}</NavLink>
                  <button
                    type="button"
                    className="dropdown-item danger workspace-profile-logout"
                    role="menuitem"
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                  >
                    <LogOut size={15} />{uiText(" Выйти")}</button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <div className={`workspace-app-frame ${showSidebar ? 'has-sidebar' : 'without-sidebar'} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {(
          <>
            <button
              type="button"
              className={`workspace-sidebar-backdrop ${mobileNavOpen ? 'is-open' : ''}`}
              onClick={() => setMobileNavOpen(false)}
              aria-label={uiText("Закрыть навигацию")}
            />
            <aside className={`workspace-module-sidebar${showSidebar ? '' : ' workspace-mobile-only-sidebar'} ${mobileNavOpen ? 'is-open' : ''}`} aria-label={uiText("Раздел «{v0}»", {v0: (currentModule?.label || uiText("Навигация"))})}>
              <div className="workspace-sidebar-header">
                {!sidebarCollapsed ? (
                  <div className="workspace-sidebar-heading">
                    <div className="workspace-sidebar-eyebrow">{uiText("Текущий модуль")}</div>
                    <div className="workspace-sidebar-title">{currentModule?.label}</div>
                    {currentModule?.description ? (
                      <div className="workspace-sidebar-description">{currentModule.description}</div>
                    ) : null}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="workspace-sidebar-collapse"
                  onClick={() => setSidebarCollapsed((value) => !value)}
                  aria-label={sidebarCollapsed ? uiText("Развернуть боковую панель") : uiText("Свернуть боковую панель")}
                >
                  {sidebarCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
                </button>
                <button
                  type="button"
                  className="workspace-sidebar-mobile-close"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label={uiText("Закрыть меню")}
                >
                  <X size={18} />
                </button>
              </div>

              <nav className="workspace-sidebar-mobile-modules" aria-label={uiText("Основные разделы")}>
                <div className="workspace-sidebar-group-label">{uiText("Основные разделы")}</div>
                <div className="workspace-sidebar-mobile-module-grid">
                  {modules.map((module) => {
                    const Icon = module.icon;
                    const active = !isStandaloneUtilityPage && currentModule?.key === module.key;
                    return (
                      <NavLink
                        key={`mobile-${module.key}`}
                        to={module.to}
                        className={`workspace-sidebar-mobile-module ${active ? 'active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        <Icon size={18} />
                        <span>{module.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </nav>

              {showSidebar ? <nav className="workspace-sidebar-nav">
                {Object.entries(sidebarGroups).map(([group, items]) => (
                  <div className="workspace-sidebar-group" key={group}>
                    {!sidebarCollapsed ? <div className="workspace-sidebar-group-label">{group}</div> : null}
                    <div className="workspace-sidebar-group-links">
                      {items.map((item) => (
                        <SidebarLink
                          key={item.to}
                          {...item}
                          collapsed={sidebarCollapsed}
                          pathname={location.pathname}
                          onClick={() => setMobileNavOpen(false)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </nav> : null}
            </aside>
          </>
        )}

        <main id="main-content" className="main-content workspace-main-content" tabIndex="-1">
          {breadcrumbs?.length > 1 ? (
            <div className="workspace-navigation-context">
              <button
                type="button"
                className="workspace-back-button"
                onClick={() => {
                  if ((window.history.state?.idx || 0) > 0) navigate(-1);
                  else navigate(currentModule?.to || '/dashboard');
                }}
                aria-label={uiText("Вернуться на предыдущую страницу")}
              >
                <ChevronRight size={14} aria-hidden="true" />
                <span>{uiText("Назад")}</span>
              </button>
              <nav className="workspace-breadcrumbs" aria-label={uiText("Хлебные крошки")}>
              <NavLink to="/dashboard" className="workspace-breadcrumb-home" aria-label={uiText("Главная")}>
                <Home size={13} />
              </NavLink>
              {breadcrumbs.map((crumb, index) => {
                const last = index === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={`${crumb.to}-${crumb.label}`}>
                    <ChevronRight size={13} className="workspace-breadcrumb-separator" />
                    {last ? (
                      <span className="workspace-breadcrumb-current" aria-current="page">{crumb.label}</span>
                    ) : (
                      <NavLink to={crumb.to} className="workspace-breadcrumb-link">{crumb.label}</NavLink>
                    )}
                  </React.Fragment>
                );
              })}
              </nav>
            </div>
          ) : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
